import {createAsyncThunk, createEntityAdapter, createSelector, createSlice} from "@reduxjs/toolkit";
import {billingProfileKey} from "./entities";
import {callGraphQL} from "./api.slice";

export const fetchBillingProfiles = createAsyncThunk(`${billingProfileKey}/fetchBillingProfiles`, async ({facilityId, promoCode, startDate}, context) => {
    const request = {
        query: `query listBillingProfilesForPurchase ($facilityId: String!, $promoCode: String, $startDate: String) {
            listBillingProfilesForPurchase(facility_id: $facilityId, promo_code: $promoCode, start_date: $startDate) {
                id
                name
                next_steps
                facility_id
                billing_day
                billing_timing
                provide_survey
                payment_due_day
                decline_recurring
                termination_notice_day
                secondMonthPayment
                survey_questions {
                    id
                    billing_profile_id
                    type
                    structure
                    order
                }
                facility {
                    id
                    uid
                    timezone
                    facility_name
                    address
                    terms_and_conditions
                    terms_and_conditions_summary
                    use_ach
                    use_credit_card
                }
                rates {
                    id
                    name
                    description
                    active
                    display
                    pass_type
                    access_hours
                    location
                    require_car_info
                    access_device_message
                    display_price
                    billing_profile_id
                    first_month_proration
                    first_month_proration_day
                    months_to_sell_in_advance
                    promo_codes {
                        id
                        rate_id
                        text
                        use_count
                        use_limit
                        expires_on
                        used_on
                        is_active
                        created_at
                        updated_at
                    },
                    access_devices {
                        id
                        type
                        required
                        offered
                        fee { ...fee_fields }
                    },
                    current_fees { ...fee_fields },
                    current_monthly_fee { ...fee_fields },
                    current_activation_fee { ...fee_fields },
                    current_credit_card_fee { ...fee_fields },
                    current_mail_invoice_fee { ...fee_fields }
                }
            }
        }
        fragment fee_fields on fee {
            id,
            type,
            price,
            tax_1,
            tax_2,
            tax_3,
            tax_4,
            payment_timing,
            start_date,
            display_name,
            created_at,
            updated_at
        }`,
        variables: {facilityId, promoCode, startDate},
        isPublic: true,
        response: {listBillingProfilesForPurchase: [billingProfileKey]}
    }
    const {type, payload, error} = await context.dispatch(callGraphQL(request));
    return type.endsWith('rejected') ? context.rejectWithValue(payload || error) : payload;
}, {
    condition: (arg, api) => !selectBillingProfile(api.getState()).loading
})
const adapter = createEntityAdapter();
const slice = createSlice({
    name: billingProfileKey,
    initialState: adapter.getInitialState({
        variables: {
            facilityId: null,
            promoCode: null
        },
        loaded: false,
        loading: false,
        errors: null
    }),
    reducers: {},
    extraReducers: {
        [fetchBillingProfiles.pending]: (state, action) => {
            state.loaing = true;
            state.errors = null;
            state.loaded = false;
            state.variables = action.arg
        },
        [fetchBillingProfiles.rejected]: (state, action) => {
            state.loading = false;
            state.errors = action.payload;
        },
        [fetchBillingProfiles.fulfilled]: (state) => {
            state.loading = false;
            state.errors = null;
            state.loaded = true;
        },
        [callGraphQL.fulfilled]: (state, {payload}) => {
            if (payload[billingProfileKey]) adapter.upsertMany(state, payload[billingProfileKey]);
        }
    }
})
export default slice.reducer;
export const selectBillingProfile = state => state[billingProfileKey];
export const {
    selectById: selectBillingProfileById,
    selectIds: selectBillingProfileIds,
    selectEntities: selectBillingProfileEntities,
    selectAll: selectAllBillingProfiles,
    selectTotal: selectTotalBillingProfiles
} = adapter.getSelectors(selectBillingProfile)
export const isFetchBillingProfilesLoaded = () => createSelector(
    selectBillingProfile,
    state => !!state.loaded
);
export const isFetchBillingProfilesLoading = () => createSelector(
    selectBillingProfile,
    state => !!state.loading
);
export const getFetchBillingProfilesErrors = () => createSelector(
    selectBillingProfile,
    (state) => state.errors ? state.errors : []
)
